import { createFileRoute } from '@tanstack/react-router'
import { Activity } from 'lucide-react'
import { useCallback, useEffect, useRef, useDeferredValue } from 'react'
import type { ColumnConfig } from '../components/ColumnSelector'
import { ColumnSelector } from '../components/ColumnSelector'
import { OptionChainTable } from '../components/tables/OptionChainTable'
import { LoadingState } from '../components/ui/LoadingState'
import { STORAGE_KEYS } from '../config/constants'
import { useColumnVisibility } from '../hooks/useColumnVisibility'
import { useDashboardData } from '../hooks/useDashboardData'
import { usePageTitle } from '../hooks/usePageTitle'
import { useSettingsStore } from '../stores/settingsStore'
import type { OptionData } from '../types'

export const Route = createFileRoute('/_auth/home')({
    component: HomeRoute,
})

const DEFAULT_COLUMNS: ColumnConfig[] = [
    { id: 'oi', label: 'OI (Lakhs)', visible: true },
    { id: 'oi_change', label: 'Chng in OI (Lakhs)', visible: true },
    { id: 'volume', label: 'Volume', visible: true },
    { id: 'iv', label: 'IV %', visible: true },
    { id: 'ltp', label: 'LTP', visible: true },
    { id: 'change_p', label: 'Chng %', visible: true },
    { id: 'vega', label: 'Vega', visible: false },
    { id: 'theta', label: 'Theta', visible: false },
    { id: 'gamma', label: 'Gamma', visible: false },
    { id: 'delta', label: 'Delta', visible: false },
    { id: 'combined_oi', label: 'Combined OI', visible: true },
    { id: 'combined_oi_change', label: 'Combined OI Chg', visible: true },
    { id: 'combined_oi_change_p', label: 'Combined OI Chg %', visible: true },
    { id: 'combined_volume', label: 'Combined Volume', visible: true },
    { id: 'combined_vol_change', label: 'Combined Vol Chg', visible: true },
    { id: 'combined_vol_change_p', label: 'Combined Vol Chg %', visible: true },
    { id: 'vol_diff', label: 'Vol Diff (CE - PE)', visible: true },
    { id: 'oi_diff', label: 'OI Diff (CE - PE)', visible: true },
]

function HomeRoute() {
    usePageTitle('Option Chain')
    // FREEZE FIX v3: Granular selectors — only subscribe to fields actually used in JSX.
    // Previously `useSettingsStore()` subscribed to ALL 30+ fields, causing re-renders
    // on every unrelated change (theme toggle, subTab switch, baseTime update, etc.)
    const symbol = useSettingsStore(s => s.symbol)
    const expiry = useSettingsStore(s => s.expiry)
    const mode = useSettingsStore(s => s.mode)
    const setExpiry = useSettingsStore(s => s.setExpiry)

    // FREEZE FIX: Defer the parameters passed to heavy sub-components
    const deferredSymbol = useDeferredValue(symbol)
    const deferredExpiry = useDeferredValue(expiry)
    const deferredMode = useDeferredValue(mode)

    // Use centralized column visibility hook with lazy initialization
    const [columns, toggleColumn, isVisible] = useColumnVisibility(STORAGE_KEYS.HOME_COLUMNS, DEFAULT_COLUMNS)

    // 1. Live Data
    const {
        marketData,
        analysis,
        effectiveExpiry,
        isLoading: isMarketLoading,
        filteredOptions,
        maxValues,
        baseOptions,
    } = useDashboardData()

    // 🚀 PERF: Removed useDeferredValue to fix live data starvation issue where 2-sec ticks were continually interrupting the deferred render
    const deferredMarketData = marketData
    const deferredFilteredOptions = filteredOptions
    const deferredAnalysis = analysis
    const deferredMaxValues = maxValues

    // FREEZE FIX v3: Use ref to prevent circular mutation loop.
    // Previously: effectiveExpiry changes → setExpiry() → settings store changes
    // → HomeRoute re-renders → effectiveExpiry check → setExpiry() again.
    // Using a ref breaks the dependency cycle.
    const expiryRef = useRef(expiry)
    expiryRef.current = expiry
    useEffect(() => {
        if (effectiveExpiry && effectiveExpiry !== expiryRef.current) {
            setExpiry(effectiveExpiry)
        }
    }, [effectiveExpiry, setExpiry])

    return (
        <div className="space-y-6 pb-20 animate-in fade-in duration-500">
                {isMarketLoading && !marketData ? (
                    <LoadingState message={`Loading ${symbol} Data...`} />
                ) : marketData && (filteredOptions || []).length > 0 ? (
                    <div className="bg-white dark:bg-slate-900 rounded-[20px] shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden transition-colors duration-300">
                        {/* Unified Table Header */}
                        <div className="px-6 py-5 flex flex-wrap gap-y-4 justify-between items-center border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 transition-colors duration-300">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-indigo-50 dark:bg-indigo-900/30 rounded-xl text-indigo-600 dark:text-indigo-400 shadow-sm ring-1 ring-indigo-100 dark:ring-indigo-800">
                                    <Activity size={22} strokeWidth={2.5} />
                                </div>
                                <div>
                                    <h2 className="text-lg font-black text-slate-800 dark:text-slate-100 tracking-tight">
                                        Option Chain
                                    </h2>
                                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5 font-medium">
                                        {deferredSymbol} • {deferredExpiry || 'Expiry'} • {deferredMode} Mode
                                    </p>
                                </div>
                            </div>

                            <div className="flex flex-1 justify-center mx-2 lg:mx-4 order-3 lg:order-none w-full lg:w-auto mt-2 lg:mt-0">
                                <div className="flex items-center gap-2 p-1.5 bg-slate-100/70 dark:bg-slate-800/50 rounded-2xl border border-slate-200/80 dark:border-slate-700/80 shadow-inner backdrop-blur-md">
                                    <div className="px-5 py-2 bg-white dark:bg-slate-900 shadow-sm rounded-xl border border-slate-200/60 dark:border-slate-700/60 flex items-center justify-center min-w-[110px]">
                                        <span className="font-black text-slate-800 dark:text-slate-100 tracking-tight text-[18px] tabular-nums">
                                            {deferredMarketData?.spotPrice?.toFixed(2) || '0.00'}
                                        </span>
                                    </div>
                                    <div className={`px-4 py-2 rounded-xl flex items-center justify-center min-w-[90px] shadow-sm border border-transparent ${(deferredMarketData?.dayChange || 0) >= 0 ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-100/50 dark:border-emerald-500/20' : 'bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-100/50 dark:border-rose-500/20'}`}>
                                        <span className="font-extrabold tracking-tight text-[16px] tabular-nums">
                                            {(deferredMarketData?.dayChange || 0) > 0 ? '+' : ''}{deferredMarketData?.dayChange?.toFixed(2) || '0.00'}
                                        </span>
                                    </div>
                                    <div className={`px-4 py-2 rounded-xl flex items-center justify-center min-w-[85px] shadow-sm border border-transparent ${(deferredMarketData?.dayChangeP || 0) >= 0 ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-100/50 dark:border-emerald-500/20' : 'bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-100/50 dark:border-rose-500/20'}`}>
                                        <span className="font-extrabold tracking-tight text-[16px] tabular-nums">
                                            {(deferredMarketData?.dayChangeP || 0) > 0 ? '+' : ''}{deferredMarketData?.dayChangeP?.toFixed(2) || '0.00'}%
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <ColumnSelector columns={columns} onChange={toggleColumn} />
                        </div>

                        <OptionChainTable
                            marketData={deferredMarketData}
                            filteredOptions={deferredFilteredOptions || []}
                            baseOptions={baseOptions || []}
                            analysis={deferredAnalysis}
                            maxValues={deferredMaxValues}
                            isVisible={isVisible}
                            className="border-none shadow-none rounded-none"
                        />
                    </div>
                ) : (
                    <div className="bg-white/60 dark:bg-slate-900/60 backdrop-blur-xl rounded-2xl border border-white/40 dark:border-slate-800/40 p-8">
                        <p className="text-center text-slate-500 dark:text-slate-400">
                            No data available. Please check your connection or try again.
                        </p>
                    </div>
                )}
            </div>
    )
}
